/**
 * <FloatingSlot floating> — ce qui flotte au coin bas d'un enchaînement de pages, posé UNE fois
 * (template).
 *
 * Une page posait ce qui flotte dans son propre gabarit : d'une étape à l'autre (`<StepStage>`), la
 * page qui entre est un autre composant, et ce qui flottait était démonté puis remonté avec elle,
 * vidéo comprise. La fente le pose au-dessus des pages, une fois, aux bords de la zone, et ne dit aux
 * pages que deux choses :
 *  - **la place à garder** (`useFloatingReserve`) : la hauteur mesurée de ce qui flotte, zéro hors
 *    d'une fente ou quand rien ne flotte. Une page la garde au pied de sa zone, hors de ce qui défile,
 *    là où elle a des boutons : sinon, un bouton visible avant la fin du défilement passe dessous ;
 *  - **le bandeau du bas** au-dessus duquel il se pose (`useFloatingBase`) : le pied d'un gabarit,
 *    la barre de copie, « J'ai fini ». Une page déclare le sien tant qu'il est là.
 *
 * Elle ne sait pas ce qu'elle porte : ce qui flotte se place lui-même dans la largeur (la bulle se
 * range à droite). Sans `floating`, elle ne pose rien et ne réserve rien, et ses enfants restent
 * montés : ce qui va et vient ne remonte pas les pages.
 */
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

type FloatingRoom = {
    /** La place à garder : la hauteur de ce qui flotte, zéro sans rien. */
    reserve: number
    /** Un bandeau du bas se déclare (sa hauteur), ou se retire (`null`). */
    setBase: (id: string, height: number | null) => void
}

const FloatingRoomContext = createContext<FloatingRoom | null>(null)

export type FloatingSlotProps = {
    /** Ce qui flotte au coin bas. Absent : rien ne flotte, rien n'est réservé. */
    floating?: React.ReactNode
    children: React.ReactNode
}

export function FloatingSlot({ floating, children }: FloatingSlotProps) {
    const [height, setHeight] = useState(0)
    const [bases, setBases] = useState<Record<string, number>>({})
    const setBase = useCallback((id: string, next: number | null) => {
        setBases((previous) => {
            if (next == null) {
                if (!(id in previous)) return previous
                const rest = { ...previous }
                delete rest[id]
                return rest
            }
            return previous[id] === next ? previous : { ...previous, [id]: next }
        })
    }, [])
    const shown = floating != null
    const reserve = shown ? height : 0
    const room = useMemo(() => ({ reserve, setBase }), [reserve, setBase])
    const base = Math.max(0, ...Object.values(bases))

    return (
        <FloatingRoomContext.Provider value={room}>
            <View style={styles.fill}>
                {children}
                {shown && (
                    <View
                        style={styles.floating(base)}
                        pointerEvents="box-none"
                        onLayout={(event) => {
                            const next = event.nativeEvent.layout.height
                            setHeight((previous) => (previous === next ? previous : next))
                        }}
                    >
                        {floating}
                    </View>
                )}
            </View>
        </FloatingRoomContext.Provider>
    )
}

/** L'écart gardé au-dessus de ce qui flotte, en plus de sa hauteur, là où une page lui garde sa place. */
export const FLOATING_RESERVE_GAP = "6" as const

/** La place à garder pour ce qui flotte : zéro hors d'une fente, ou sans rien. */
export function useFloatingReserve(): number {
    return useContext(FloatingRoomContext)?.reserve ?? 0
}

/**
 * Déclare un bandeau du bas : ce qui flotte se pose au-dessus de lui. Rend le lecteur de sa hauteur,
 * à appeler à chaque mise en page ; `present` à `false` (le bandeau n'est plus là) ou le démontage le
 * retirent. Hors d'une fente, rien.
 */
export function useFloatingBase(present: boolean): (height: number) => void {
    const setBase = useContext(FloatingRoomContext)?.setBase
    const id = useId()
    useEffect(() => {
        if (setBase == null) return undefined
        return () => setBase(id, null)
    }, [setBase, id, present])
    return useCallback((height: number) => setBase?.(id, height), [setBase, id])
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1, minHeight: 0 },
    /** Au coin bas de la zone, aux bords, au-dessus du bandeau du bas. */
    floating: (base: number) => ({
        position: "absolute" as const,
        left: theme.spacing["4"],
        right: theme.spacing["4"],
        bottom: base + theme.spacing["3"],
    }),
}))
